/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        1) Do not import the following modules into this module:
            import {
                < any enumeration >
            } from '/ezlibrary/enums/EzEnumerations.js';
            import {
                < any helper classes >
            } from '/ezlibrary/helpers/EzHelpers.js';
            import {
                < any other exception classes >
            } from '/ezlibrary/exceptions/EzExceptions.js';
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

/**
 * @class
 * @extends {Error}
 * @description
 * Top level exception for ezClocker website
 * EzException is the base Exception class for all ezClocker exceptions.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * ENGINEERING NOTES:
 *     1) Do not import this class into /ezlibrary/ezclocker-classes/EzClasses.js
 *        import group module because EzException is alredy exported by the
 *        /ezlibrary/exceptions/EzExceptions.js import group module.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import {
 *         EzException,
 *         // EzExceptionInClassMethod,
 *         // EzBadParamException,
 *         // EzBadStateException,
 *         // EzStaticClassException,
 *         // EzNotSupportedException
 *     } from '/ezlibrary/exceptions/EzExceptions.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzException extends Error {
    /**
     * @static
     * @public @readonly @property
     * Gets the default EzException error mesage: 'An unexpected error occurred.'
     * @returns {string}
     */
    static get DEFAULT_ERROR_MESSAGE() {
        return 'An unexpected error occurred.';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default exception options object:
     *  {
     *      errorCode: 500,
     *      cause: null
     *  }
     * @type {object}
     */
    static get DEFAULT_EXCEPTION_OPTIONS() {
        return {
            errorCode: 500,
            cause: null
        };
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default error code constant: 500
     */
    static get DEFAULT_ERROR_CODE() {
        return 500;
    }

    /**
     * @static
     * @public @method
     * Builds the default options object to pass to an exception:
     *  {
     *      cause: exceptionInstance?.cause || undefined,
     *      name: exceptionInstance?.constructor.name || 'EzClockerException',
     *      fileName: exceptionInstance?.fileName || undefined,
     *      lineNumber: exceptionInstance?.lineNumber || undefined,
     *      stack: exceptionInstance?.stack || undefined
     *  };
     */
    static ezBuildDefaultOptions(exceptionInstance = null) {
        if (!exceptionInstance) {
            throw new Error(
                `A valid exceptionInstance param is required in call to static method: ${EzException.ezBuildDefaultOptions.toString()}`);
        }

        return {
            cause: exceptionInstance?.cause || undefined,
            name: exceptionInstance?.constructor.name || 'EzClockerException',
            fileName: exceptionInstance?.fileName || undefined,
            lineNumber: exceptionInstance?.lineNumber || undefined,
            stack: exceptionInstance?.stack || undefined
        };
    }

    /**
     * @public @constructor     * Creates a new instance of EzException
     * @param {undefined|null|string} message
     * Default is EzException.DEFAULT_ERROR_MESSAGE
     * @param {undefined|null|object|any} options
     * Default is: null
     */
    constructor(message = EzException.DEFAULT_ERROR_MESSAGE, options = EzException.DEFAULT_EXCEPTION_OPTIONS) {
        super(
            'string' === typeof message && 0 < message?.length
                ? message
                : EzException.DEFAULT_ERROR_MESSAGE,
            options || null);

        this.message = 'string' === typeof message && 0 < message?.length
            ? message
            : EzException.DEFAULT_ERROR_MESSAGE;

        if (options) {
            if ('string' === typeof options) {
                this.fileName = options;
            } else if ('number' === typeof options) {
                this.lineNumber = options;
            } else {
                // Load properties from the provided options object
                this.fileName = 0 < options?.name?.length
                    ? options.fileName
                    : undefined;

                this.lineNumber = options?.lineNumber
                    ? options.lineNumber
                    : undefined;

                if (0 > this.stack.length && 0 > options?.stack?.length) {
                    this.stack = `${this.stack} ${options.stack}`;
                } else {
                    this.stack = options?.stack?.length
                        ? options.stack
                        : undefined;
                }

                this.name = 0 < options?.name?.length
                    ? options.name
                    : this?.constructor?.name || 'EzClockerException';

                this.errorCode = options?.errorCode
                    ? options.errorCode
                    : EzException.DEFAULT_ERROR_CODE;
            }
        } else {
            this.name = this?.constructor?.name || 'EzClockerException';

            this.errorCode = EzException.DEFAULT_ERROR_CODE;
        }

        this.options = options || EzException.DEFAULT_EXCEPTION_OPTIONS;
    }

    /**
     * @private @field
     * Stores the exception's error code
     * Default is: 500
     * @type {number}
     */
    #errorCode = EzException.DEFAULT_ERROR_CODE;
    /**
     * @public @property @getter
     * Gets the exception's error code
     * @returns {number}
     */
    get errorCode() {
        return this.#errorCode;
    }
    /**
     * @public @property @setter
     * Sets the exception's error code
     * @param {number}
     * Default: EzException.DEFAULT_ERROR_CODE
     */
    set errorCode(errorCode) {
        this.#errorCode = errorCode || EzException.DEFAULT_ERROR_CODE;
    }

    /**
     * @public @property @getter
     * Gets the cause of this exception (a caught Error)
     * @param {null|object}
     */
    get cause() {
        return super.cause;
    }
    /**
     * @public @property @setter
     * Sets the cause of this exception (a caught Error)
     * @param {undefined|null|object} cause
     */
    set cause(aCause) {
        super.cause = aCause || null;

        this.options.cause = super.cause;
    }

    /**
     * @public @property @getter
     * Gets the message for this exception
     * @returns {string}
     */
    get message() {
        return super.message;
    }
    /**
     * @public @property @setter
     * Sets the message for this exception
     * @param {string} aMessage
     */
    set message(aMessage) {
        super.message = 'string' === typeof aMessage && 0 < aMessage.length
            ? aMessage
            : EzException.DEFAULT_ERROR_MESSAGE;
    }

    /**
     * @public @property @getter
     * Gets the stack trace for the error
     * @returns {string}
     */
    get stack() {
        return super.stack;
    }
    /**
     * @public @property @setter
     * Sets the message for this exception
     * @param {string} stack
     */
    set stack(aStack) {
        super.stack = aStack || null;
    }

    /**
     * @private @field
     * Stores the options provided to the exception
     * Default: EzException.DEFAULT_EXCEPTION_OPTIONS
     * @type {object}
     */
    #options = EzException.DEFAULT_EXCEPTION_OPTIONS;
    /**
     * @public @property @getter
     * @type {object}
     */
    get options() {
        return this.#options;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {undefined|null|object} aOptions}
     * Default: EzException.DEFAULT_EXCEPTION_OPTIONS
     */
    set options(aOptions = EzException.DEFAULT_EXCEPTION_OPTIONS) {
        this.#options = aOptions || EzException.DEFAULT_EXCEPTION_OPTIONS;

        this.#options.cause = aOptions.cause || aOptions.causeBy || null;

        this.#options.errorCode = aOptions.errorCode || 500;

        this.errorCode = aOptions?.errorCode || 500;

        this.cause = aOptions?.cause || aOptions?.causedBy || null;
    }

    /**
     * @public @readonly @method
     * Gets this exception as a string
     * Template: `${this.name}: ${this.message} (${this.errorCode}) [Stack: ${this.stack}]`
     * @returns {string}
     */
    get toString() {
        return `${this.name}: ${this.message} (${this.errorCode}) [Stack: ${this.stack}]`;
    }
}

